import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { getApiUrl } from '../config/api';
import { useAuth } from '../App';
import toast from 'react-hot-toast';

const TransactionList = () => {
  const { user } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => {
    fetchTransactions();
    // eslint-disable-next-line
  }, []);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const res = await axios.get(getApiUrl('/wallet/transactions'));
      setTransactions(res.data);
    } catch (err) {
      toast.error('Không thể tải danh sách giao dịch');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id) => {
    try {
      await axios.patch(getApiUrl(`/wallet/transaction/${id}/approve`));
      toast.success('Duyệt thành công!');
      await fetchTransactions(); // Refresh data immediately
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Duyệt thất bại!');
    }
  };

  const handleReject = async (id) => {
    if (!rejectReason.trim()) {
      toast.error('Vui lòng nhập lý do từ chối');
      return;
    }
    try {
      await axios.patch(getApiUrl(`/wallet/transaction/${id}/reject`), { reason: rejectReason });
      toast.success('Từ chối thành công!');
      setRejectingId(null);
      setRejectReason('');
      await fetchTransactions(); // Refresh data immediately
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Từ chối thất bại!');
    }
  };

  if (loading) return <div>Đang tải...</div>;

  const formatAmount = (amount, type) => {
    const formattedAmount = new Intl.NumberFormat('vi-VN').format(amount);
    if (type === 'WITHDRAW') {
      return <span className="text-red-600 font-semibold">-{formattedAmount} VND</span>;
    } else {
      return <span className="text-green-600 font-semibold">+{formattedAmount} VND</span>;
    }
  };

  const getTransactionTypeText = (type) => {
    switch (type) {
      case 'DEPOSIT': return 'Nạp tiền';
      case 'WITHDRAW': return 'Rút tiền';
      case 'ADJUST_CREDIT': return 'Điều chỉnh';
      default: return type;
    }
  };

  return (
    <div className="bg-white p-6 rounded shadow">
      <h2 className="text-xl font-bold mb-4">Quản lý giao dịch ví</h2>
      <div className="overflow-x-auto">
        <table className="min-w-full border">
          <thead>
            <tr>
              <th className="border px-2 py-1">Mã giao dịch</th>  
              <th className="border px-2 py-1">User</th>
              <th className="border px-2 py-1">Loại</th>
              <th className="border px-2 py-1">Số tiền</th>
              <th className="border px-2 py-1">Mô tả</th>
              <th className="border px-2 py-1">Thông tin NH</th>
              <th className="border px-2 py-1">Thời gian</th>
              <th className="border px-2 py-1">Trạng thái</th>
              <th className="border px-2 py-1">Thao tác</th>
            </tr>
          </thead>
          <tbody>
            {transactions.map(tx => (
              <tr key={tx.id}>
                <td className="border px-2 py-1">{tx.transaction_code || `#${tx.id}`}</td>
                <td className="border px-2 py-1">{tx.user_id || tx.wallet_id}</td>
                <td className="border px-2 py-1">
                  <span className={`px-2 py-1 rounded text-xs font-semibold ${
                    tx.type === 'DEPOSIT' ? 'bg-green-100 text-green-800' : 
                    tx.type === 'WITHDRAW' ? 'bg-red-100 text-red-800' : 
                    'bg-blue-100 text-blue-800'
                  }`}>
                    {getTransactionTypeText(tx.type)}
                  </span>
                </td>
                <td className="border px-2 py-1">{formatAmount(tx.amount, tx.type)}</td>
                <td className="border px-2 py-1">{tx.description}</td>
                <td className="border px-2 py-1 text-xs">
                  {tx.type === 'WITHDRAW' && tx.bank_name ? (
                    <div className="space-y-1">
                      <div><strong>NH:</strong> {tx.bank_name}</div>
                      <div><strong>STK:</strong> {tx.bank_account_number}</div>
                      <div><strong>CTK:</strong> {tx.bank_account_holder}</div>
                      {tx.bank_branch && <div><strong>CN:</strong> {tx.bank_branch}</div>}
                    </div>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
                <td className="border px-2 py-1 text-sm">{new Date(tx.created_at).toLocaleString('vi-VN')}</td>
                <td className="border px-2 py-1">
                {tx.approved_by ? (
                  <span className="text-green-600">Đã duyệt</span>
                ) : tx.rejected_by ? (
                  <span className="text-red-600">Đã từ chối{tx.reject_reason ? `: ${tx.reject_reason}` : ''}</span>
                ) : (
                  <span className="text-yellow-600">Chờ duyệt</span>
                )}
              </td>
              <td className="border px-2 py-1">
                {!tx.approved_by && !tx.rejected_by && user && user.roles.some(r => r.name === 'super_admin' || r.name === 'admin') && (
                  <>
                    <button onClick={() => handleApprove(tx.id)} className="bg-blue-600 text-white px-2 py-1 rounded mr-2">Duyệt</button>
                    {rejectingId === tx.id ? (
                      <span>
                        <input
                          type="text"
                          placeholder="Lý do từ chối"
                          value={rejectReason}
                          onChange={e => setRejectReason(e.target.value)}
                          className="border px-1 py-0.5 rounded mr-1"
                        />
                        <button onClick={() => handleReject(tx.id)} className="bg-red-600 text-white px-2 py-1 rounded">Xác nhận</button>
                        <button onClick={() => { setRejectingId(null); setRejectReason(''); }} className="ml-1 text-gray-500">Hủy</button>
                      </span>
                    ) : (
                      <button onClick={() => setRejectingId(tx.id)} className="bg-gray-400 text-white px-2 py-1 rounded">Từ chối</button>
                    )}
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
        </table>
      </div>
    </div>
  );
};

export default TransactionList;
